/**
 * UI state. The authoritative session state lives in the FieldEngine (see
 * session/runtime.ts); this store mirrors snapshots for rendering and holds
 * view-level flags (phase, seams overlay, sensing readout).
 */

import { create } from 'zustand';
import type { DriftMap, Fragment, RegimeName, Rhizome, Tactic } from '@wintermute/orchestrator';
import type { AffectFeatures } from '../sensing/faceSensing.js';

/**
 * `rite` replaces the old `intro` form. There is no permissions page any more:
 * WinterMute is present from the moment the link opens and asks for each grant
 * itself, one at a time. See `rite/script.ts` for why a touch still has to sit
 * between the asking and the native prompt.
 */
export type Phase = 'rite' | 'field' | 'reflection' | 'ending';

export interface WinterMuteState {
  phase: Phase;
  live: Fragment[];
  regime: RegimeName;
  dominant: Tactic[];
  seams: boolean;
  showScan: boolean;
  showSlamHud: boolean;
  /** The aged-panel treatment on the camera feed. See visual/postfx/ground.ts. */
  ground: boolean;
  demo: boolean;
  sensingConsent: boolean;
  /** Separate, explicit consent for the cloud VLM watcher (frames leave device). */
  watcherConsent: boolean;
  /** True while a frame is being sent to / processed by the watcher. */
  watcherLooking: boolean;
  /**
   * The most recent watcher observation. `object` is the real thing it named in
   * the frame, which is what makes its presence provable rather than atmospheric.
   */
  lastObservation: {
    text: string;
    tactic: string;
    object?: string | null;
    /** 'stub' means the model never ran. Never let that look like success. */
    via?: 'llm' | 'stub';
    reason?: string;
    /** How the Watcher described its own posture, self-reported — see
     *  REGISTER_VOCABULARY in server/src/observe.ts. Descriptive only. */
    register?: string;
  } | null;
  /** Bumped each time the Watcher starts looking, to trigger the Sun's blink. */
  watcherBlink: number;
  /** The controls drawer, top-left. Debug lives here, not over the camera. */
  drawerOpen: boolean;
  affect: AffectFeatures | null;
  drift: DriftMap | null;
  rhizome: Rhizome | null;
  lastMutation: { op: string; text: string } | null;
  cameraError: string | null;

  setPhase: (p: Phase) => void;
  setLive: (f: Fragment[]) => void;
  setRegime: (r: RegimeName) => void;
  setDominant: (t: Tactic[]) => void;
  toggleSeams: () => void;
  toggleScan: () => void;
  toggleSlamHud: () => void;
  toggleGround: () => void;
  setDemo: (d: boolean) => void;
  setSensingConsent: (c: boolean) => void;
  setWatcherConsent: (c: boolean) => void;
  setWatcherLooking: (l: boolean) => void;
  setLastObservation: (
    o: {
      text: string;
      tactic: string;
      object?: string | null;
      via?: 'llm' | 'stub';
      reason?: string;
      register?: string;
    } | null,
  ) => void;
  bumpWatcherBlink: () => void;
  toggleDrawer: () => void;
  setDrawerOpen: (o: boolean) => void;
  setAffect: (a: AffectFeatures | null) => void;
  setDrift: (d: DriftMap | null) => void;
  setRhizome: (r: Rhizome | null) => void;
  setLastMutation: (m: { op: string; text: string } | null) => void;
  setCameraError: (e: string | null) => void;
}

export const useStore = create<WinterMuteState>((set) => ({
  phase: 'rite',
  live: [],
  regime: 'neutral',
  dominant: [],
  seams: false,
  showScan: true,
  showSlamHud: false,
  ground: true,
  demo: false,
  sensingConsent: false,
  watcherConsent: false,
  watcherLooking: false,
  lastObservation: null,
  watcherBlink: 0,
  drawerOpen: false,
  affect: null,
  drift: null,
  rhizome: null,
  lastMutation: null,
  cameraError: null,

  setPhase: (phase) => set({ phase }),
  setLive: (live) => set({ live }),
  setRegime: (regime) => set({ regime }),
  setDominant: (dominant) => set({ dominant }),
  toggleSeams: () => set((s) => ({ seams: !s.seams })),
  toggleScan: () => set((s) => ({ showScan: !s.showScan })),
  toggleSlamHud: () => set((s) => ({ showSlamHud: !s.showSlamHud })),
  toggleGround: () => set((s) => ({ ground: !s.ground })),
  setDemo: (demo) => set({ demo }),
  setSensingConsent: (sensingConsent) => set({ sensingConsent }),
  setWatcherConsent: (watcherConsent) => set({ watcherConsent }),
  setWatcherLooking: (watcherLooking) => set({ watcherLooking }),
  setLastObservation: (lastObservation) => set({ lastObservation }),
  bumpWatcherBlink: () => set((s) => ({ watcherBlink: s.watcherBlink + 1 })),
  toggleDrawer: () => set((s) => ({ drawerOpen: !s.drawerOpen })),
  setDrawerOpen: (drawerOpen) => set({ drawerOpen }),
  setAffect: (affect) => set({ affect }),
  setDrift: (drift) => set({ drift }),
  setRhizome: (rhizome) => set({ rhizome }),
  setLastMutation: (lastMutation) => set({ lastMutation }),
  setCameraError: (cameraError) => set({ cameraError }),
}));
